import { ref } from 'vue'
import type { ItemRow } from '~~/shared/items/itemRow'
import type { ItemDetail } from '~~/shared/items/itemDetail'
import type { ItemRecord } from '~~/shared/items/itemRecord'
import { mapWith, mapWithout, type EntityCache } from '~/stores/entityCache'
import type { ApiFetch } from '~/composables/documents/browser'

export interface DocumentDetailDeps {
  apiFetch: ApiFetch
  cache: EntityCache<ItemRow>
  trackPending: (rows: Array<Pick<ItemRow, 'id' | 'name' | 'file_name' | 'status'>>) => void
}

/**
 * The fields a listing row shares with a detail or a bare record — the ONLY ones a detail may
 * correct in the row cache. Four call sites merged these by hand; merging more would half-populate
 * a row with detail-only shapes (or with `plain_text`, which the listings deliberately strip).
 */
export function rowFieldsOf(item: Pick<ItemRow, 'name' | 'status' | 'path' | 'updated_at'>): Partial<ItemRow> {
  return { name: item.name, status: item.status, path: item.path, updated_at: item.updated_at }
}

/**
 * The fields an `ItemRecord` and an `ItemDetail` genuinely agree on.
 *
 * Spreading the whole record over a detail would add `organization_id`'s siblings (`index_job`,
 * `content_sha256`, …) to a shape that does not declare them, and would say nothing about the six
 * relation arrays it does not carry. Naming the overlap is what keeps the merge honest.
 */
function withoutRelationlessFields(item: ItemRecord): Partial<ItemDetail> {
  return {
    id: item.id,
    type: item.type,
    path: item.path,
    name: item.name,
    slug: item.slug,
    contract_type: item.contract_type,
    subject: item.subject,
    html_content: item.html_content,
    status: item.status,
    read_method: item.read_method,
    embedded_image_count: item.embedded_image_count,
    plain_text: item.plain_text,
    summary: item.summary,
    file_name: item.file_name,
    chunk_count: item.chunk_count,
    compliance_score: item.compliance_score,
    error_message: item.error_message,
    onedrive_item_id: item.onedrive_item_id,
    onedrive_drive_id: item.onedrive_drive_id,
    onedrive_url: item.onedrive_url,
    storage_path: item.storage_path,
    extracted_metadata: item.extracted_metadata,
    sort_order: item.sort_order,
    created_at: item.created_at,
    updated_at: item.updated_at,
  }
}

/**
 * Rich payloads for documents whose page has been opened — an overlay, not a replacement.
 *
 * `ItemDetail` gets its own map rather than being merged into the row: neither shape is a superset
 * of the other (see `shared/items/itemDetail.ts`), so merging would let a list refetch clobber a
 * loaded document's relations.
 */
export function useDocumentDetail({ apiFetch, cache, trackPending }: DocumentDetailDeps) {
  const details = ref<Map<string, ItemDetail>>(new Map())

  function detail(id: string): ItemDetail | null {
    return details.value.get(id) ?? null
  }

  /**
   * Load one document's rich payload. Throws so the page can render an error state.
   *
   * The detail lands in its own map, and the fields it shares with the row are merged into the cache
   * — so opening a document from a case page also corrects whatever the sidebar shows for it.
   */
  async function loadDetail(id: string): Promise<ItemDetail> {
    const item = await apiFetch<ItemDetail>(`/api/items/${id}`)
    details.value = mapWith(details.value, id, item)
    cache.patch(id, rowFieldsOf(item))
    trackPending([{ id: item.id, name: item.name, file_name: item.file_name, status: item.status }])
    return item
  }

  /**
   * Re-read one row's shared fields. Uses the detail endpoint because there is no row-by-id route;
   * only the fields `ItemRow` and `ItemDetail` agree on are merged, so this can never half-populate
   * a row with detail-only shapes.
   */
  async function refreshRow(id: string) {
    try {
      const item = await apiFetch<ItemDetail>(`/api/items/${id}`)
      cache.patch(id, rowFieldsOf(item))
    } catch (err) {
      // SAFE TO IGNORE, AND HERE IS WHY — which is the thing "tracked by useApi" was standing in
      // for and getting wrong (AlexAmin/marcus#469).
      //
      // Nothing is CLAIMED by this failing. Every other catch in this store guarded a request whose
      // absence the UI renders as a positive statement ("this folder is empty", "you have no
      // cases"); this one re-reads a row that is already on screen, so failing leaves the value the
      // server previously served. That is stale, not invented — and staleness here is bounded: the
      // only caller is the indexing poller's settled-job handler, which fires again on the next
      // settled job, and `fetchItems` refetches the whole listing alongside it.
      //
      // What was genuinely wrong was the SILENCE. A row stuck on "Procesando" long after its job
      // settled left no trace anywhere, so the console gets the cause. `warn`, not `error`: a
      // background refresh that missed is not the same event as a listing the user is looking at
      // failing to load.
      console.warn(`[documents] background refresh of /api/items/${id} failed`, err)
    }
  }

  async function refreshDetail(id: string) {
    try {
      await loadDetail(id)
    } catch (err) {
      // SAFE TO IGNORE, for `refreshRow`'s reason above: this is the fire-and-forget half of the
      // same settled-job handler, over a detail overlay that is already populated.
      //
      // The distinction that makes it safe is one line up: `loadDetail` deliberately does NOT catch,
      // so the USER-INITIATED load still throws and `/app/docs/:id` renders its own error state.
      // Swallowing belongs only to the refresh, never to the load (AlexAmin/marcus#469).
      console.warn(`[documents] background refresh of /api/items/${id} detail failed`, err)
    }
  }

  /**
   * Patch a document and land the response in both the detail overlay and the row cache.
   *
   * Replaces `useItems().updateItem`, which returned the payload and left every caller to decide
   * what to do with it — so a status change on `/app/docs/:id` updated that page and nothing else.
   */
  async function updateDetail(id: string, fields: Record<string, unknown>): Promise<ItemDetail | null> {
    // `PUT /api/items/:id` returns the bare `items` row — an `ItemRecord`, NOT an `ItemDetail`. This
    // used to be typed `ItemDetail` and stored verbatim, which dropped `parties`, `dates`, `amounts`,
    // `clauses`, `terms` and `cases` from the loaded document: cycling a document's status on the
    // metadata view left `detail.parties` undefined and `detail.parties.length` threw on the next
    // render. The response is merged into whatever detail is already loaded instead.
    const item = await apiFetch<ItemRecord>(`/api/items/${id}`, { method: 'PUT', body: fields })
    const existing = details.value.get(id)
    const merged = existing ? { ...existing, ...withoutRelationlessFields(item) } : null
    if (merged) details.value = mapWith(details.value, id, merged)
    cache.patch(id, rowFieldsOf(item))
    return merged
  }

  function forgetDetail(id: string) {
    if (!details.value.has(id)) return
    details.value = mapWithout(details.value, id)
  }

  /** Drop removed documents' details. Part of the store's `remove()`. */
  function prune(gone: Set<string>) {
    details.value = new Map([...details.value].filter(([id]) => !gone.has(id)))
  }

  return { details, detail, loadDetail, refreshRow, refreshDetail, updateDetail, forgetDetail, prune }
}
